import {
	RefObject,
	TouchEventHandler,
	useCallback,
	useEffect,
	useRef,
	useState
} from "react";
import { isNil } from "../Misc/utils";
import { useStableImperativeCallback } from "./useStableImperativeCallback";

export type LongTouchOptions = {
	handler(): void;
	timeoutInMs?: number;
};

export type LongTouchResult = {
	onTouchStart: TouchEventHandler;
	onTouchMove(): void;
	onTouchEnd: TouchEventHandler;
	isActive: boolean;
};

export function useLongTouch(options: LongTouchOptions): LongTouchResult {
	const {
		handler: handlerFromProps,
		timeoutInMs = 400
	} = options;

	const [touchId, setTouchId] = useState<number | null>(null);
	const [isActive, setIsActive] = useState<boolean>(false);
	const didLoungTouchFireInCurrentEventSequence: RefObject<boolean> = useRef<boolean>(false);
	const handler: () => void = useStableImperativeCallback(handlerFromProps);

	const touchStartHandler = useCallback(
		(args: React.TouchEvent): void => {
			setIsActive(false);
			didLoungTouchFireInCurrentEventSequence.current = false;

			const { touches } = args;
			if (touches.length === 1) {
				setTouchId(
					touches[0]
						.identifier
				);
			} else {
				setTouchId(null);
			}
		},
		[]
	);

	const touchMoveHandler = useCallback(
		(): void => setTouchId(null),
		[]
	);

	const touchEndHandler = useCallback(
		(event: React.TouchEvent): void => {
			if (didLoungTouchFireInCurrentEventSequence.current) {
				event.preventDefault();
			}

			setTouchId(null);

			setTimeout(
				() => {
					if (!didLoungTouchFireInCurrentEventSequence.current) {
						return;
					}

					didLoungTouchFireInCurrentEventSequence.current = false;

					setIsActive(false);

					window.getSelection()
						?.removeAllRanges?.();

					window.getSelection()
						?.empty?.();
				},
				10
			);
		},
		[]
	);

	useEffect(
		(): (() => void) | void => {
			if (isNil(touchId)) {
				return;
			}

			const timeoutHandle: SetTimeoutHandle = setTimeout(
				(): void => {
					didLoungTouchFireInCurrentEventSequence.current = true;
					setIsActive(true);
					handler();
				},
				timeoutInMs
			);

			const windowTouchEndHandler = (args: TouchEvent): void => {
				// For w/e reason, on android when you long touch and a "copy tooltip" appears the
				// touchend" event is not trigger on the element resulting in that this hook thinks
				// it is still touching while in fact the user already released the touch.
				// So we also add a native event on the window itself to detect when the touch
				// is released so it doesn't get stuck.
				let wasTouchIdWeAreInterestedIn: boolean = false;

				for (let i = 0; i < args.changedTouches.length; i++) {
					if (args.changedTouches[i].identifier === touchId) {
						wasTouchIdWeAreInterestedIn = true;
						break;
					}
				}

				if (wasTouchIdWeAreInterestedIn) {
					setTouchId(null);
				}
			};

			window.addEventListener("touchend", windowTouchEndHandler);

			return (): void => {
				window.removeEventListener("touchend", windowTouchEndHandler);
				clearTimeout(timeoutHandle);
			};
		},
		[
			touchId,
			handler,
			timeoutInMs
		]
	);

	return {
		isActive,
		onTouchStart: touchStartHandler,
		onTouchMove: touchMoveHandler,
		onTouchEnd: touchEndHandler
	};
}
